import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion } from "framer-motion";
import { X, RefreshCw, Truck, MapPin, Phone, Loader2, Activity, Power, PowerOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const driverIcon = (active) =>
  L.divIcon({
    className: "",
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    html: `<div style="font-size:32px;line-height:1;filter:drop-shadow(0 4px 6px rgba(0,0,0,0.4));${
      active ? "" : "opacity:0.45;"
    }">🚚</div>`,
  });

const customerIcon = L.divIcon({
  className: "",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  html: `<div style="background:#E85D45;color:white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 4px 8px rgba(0,0,0,0.3);font-size:16px;">🏪</div>`,
});

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "—";
  }
};

const LiveDriversTracking = ({ onClose }) => {
  const [drivers, setDrivers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const { data } = await axios.get(`${API}/admin/drivers/live-locations`);
      setDrivers(data || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 8000);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Admin-only kill-switch — flips a driver's GPS broadcaster ON/OFF.
  // The driver UI never exposes this, so the driver doesn't realize the
  // tracking is being controlled remotely.
  const toggleTracking = async (driver, e) => {
    e.stopPropagation();
    try {
      const target = !(driver.tracking_enabled ?? true);
      await axios.patch(`${API}/admin/drivers/${driver.id}/tracking-toggle`, { enabled: target });
      setDrivers((prev) => prev.map((d) => d.id === driver.id ? { ...d, tracking_enabled: target } : d));
    } catch { /* silent */ }
  };

  // Show every driver who's pushed coords (24/7 tracking) — not just ones
  // mid-delivery. The "is_active" badge still tells the admin who's busy.
  const onMap = drivers.filter((d) => d.lat != null && d.lng != null);
  const active = drivers.filter((d) => d.is_active && d.lat != null && d.lng != null);
  const onlySelected = selectedId ? drivers.find((d) => d.id === selectedId) : null;
  const drawableDrivers = onlySelected ? [onlySelected] : onMap;

  // Center map: first active driver, else Baghdad
  const firstWithCoords = drawableDrivers.find((d) => d.lat != null && d.lng != null);
  const center = firstWithCoords ? [firstWithCoords.lat, firstWithCoords.lng] : [33.3152, 44.3661];

  const embedded = !onClose;

  const inner = (
    <>
      {/* Header */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 animate-pulse" />
            <h3 className="font-bold text-lg">تتبّع السائقين مباشر</h3>
            <span className="bg-white/25 px-2 py-0.5 rounded-full text-[10px] font-bold" dir="ltr">
              {active.length} نشط
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              disabled={refreshing}
              data-testid="live-tracking-refresh"
              className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                data-testid="live-tracking-close"
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-10">
            <Loader2 className="w-10 h-10 animate-spin text-[#3B82F6]" />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-[300px_1fr] overflow-hidden">
            {/* Left rail: driver list */}
            <div className="border-l border-[#E4EBE6] overflow-y-auto bg-[#F8FAFC] max-h-[60vh] md:max-h-none">
              {drivers.length === 0 ? (
                <div className="p-6 text-center text-[#5C6E64] text-sm">
                  <Truck className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  لا يوجد سائقون
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  <button
                    onClick={() => setSelectedId(null)}
                    data-testid="live-tracking-all"
                    className={`w-full text-right p-2 rounded-xl text-xs font-bold transition-colors ${
                      !selectedId ? "bg-[#1E3A5F] text-white" : "bg-white text-[#1E3A5F] hover:bg-[#E0F2FE]"
                    }`}
                  >
                    عرض كل المتصلين ({onMap.length}) · نشطين ({active.length})
                  </button>
                  {drivers.map((d) => {
                    const isSel = selectedId === d.id;
                    return (
                      <button
                        key={d.id}
                        onClick={() => setSelectedId(isSel ? null : d.id)}
                        data-testid={`live-tracking-driver-${d.id}`}
                        className={`w-full text-right p-2.5 rounded-xl border-2 transition-all flex items-center gap-2 ${
                          isSel
                            ? "border-[#3B82F6] bg-[#E0F2FE]"
                            : d.is_active
                            ? "border-[#7ED321] bg-white"
                            : "border-[#E4EBE6] bg-white opacity-70"
                        }`}
                      >
                        {d.photo_path ? (
                          <img
                            src={`${API}/files/${d.photo_path}`}
                            alt={d.full_name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-white shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#F0F9FF] border-2 border-[#3B82F6] flex items-center justify-center shrink-0">
                            <Truck className="w-4 h-4 text-[#3B82F6]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-right">
                          <p className="font-bold text-xs text-[#1E3A5F] truncate">{d.full_name || "—"}</p>
                          {d.vehicle && (
                            <p className="text-[10px] text-[#3B82F6] font-bold truncate">
                              {d.vehicle.name}{d.vehicle.plate ? ` · ${d.vehicle.plate}` : ""}
                            </p>
                          )}
                          {d.is_active ? (
                            <p className="text-[10px] text-[#1c7c3a] font-bold">
                              نشط · طلب جاري
                            </p>
                          ) : (
                            <p className="text-[10px] text-[#5C6E64]">
                              {d.lat != null ? "متصل بدون طلب" : "غير متصل"}
                            </p>
                          )}
                          {d.last_seen && (
                            <p className="text-[9px] text-[#5C6E64]" dir="ltr">
                              {fmtTime(d.last_seen)}
                            </p>
                          )}
                        </div>
                        <span
                          className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                            d.is_active ? "bg-[#22C55E] animate-pulse" : d.lat != null ? "bg-[#F5A623]" : "bg-[#9CA3AF]"
                          }`}
                        />
                        <span
                          onClick={(e) => toggleTracking(d, e)}
                          role="button"
                          data-testid={`toggle-tracking-${d.id}`}
                          title={(d.tracking_enabled ?? true) ? "التتبع مُفعّل — اضغط لإيقافه (مخفي عن السائق)" : "التتبع مُغلق — اضغط لتفعيله"}
                          className={`shrink-0 p-1 rounded-full transition-colors cursor-pointer ${(d.tracking_enabled ?? true) ? "bg-[#15803D]/15 text-[#15803D] hover:bg-[#15803D]/25" : "bg-[#E85D45]/15 text-[#E85D45] hover:bg-[#E85D45]/25"}`}
                        >
                          {(d.tracking_enabled ?? true) ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: Map */}
            <div className="relative" style={{ minHeight: embedded ? "60vh" : "100%" }}>
              <MapContainer
                key={`${center[0]}-${center[1]}-${selectedId || "all"}`}
                center={center}
                zoom={12}
                style={{ height: "100%", width: "100%", minHeight: embedded ? "60vh" : undefined }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {drawableDrivers.map((d) => {
                  if (d.lat == null || d.lng == null) return null;
                  return (
                    <div key={d.id}>
                      <Marker position={[d.lat, d.lng]} icon={driverIcon(d.is_active)}>
                        <Popup>
                          <div dir="rtl" className="text-sm space-y-1">
                            <p className="font-bold">{d.full_name}</p>
                            {d.vehicle ? (
                              <p className="text-[#1E3A5F] font-bold">
                                {d.vehicle.name}{d.vehicle.plate ? ` · ${d.vehicle.plate}` : ""}
                              </p>
                            ) : d.vehicle_type && (
                              <p>{d.vehicle_type} {d.vehicle_number || ""}</p>
                            )}
                            {d.phone && (
                              <a href={`tel:${d.phone}`} className="text-[#E85D45] flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <span dir="ltr">{d.phone}</span>
                              </a>
                            )}
                            {d.active_order && (
                              <p className="text-xs">
                                طلب: <span dir="ltr">{d.active_order.id?.slice(0, 8)}</span> · {d.active_order.status}
                              </p>
                            )}
                            {d.last_seen && (
                              <p className="text-[10px] text-gray-500" dir="ltr">آخر تحديث: {fmtTime(d.last_seen)}</p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                      {d.is_active && d.customer_lat != null && d.customer_lng != null && (
                        <>
                          <Marker position={[d.customer_lat, d.customer_lng]} icon={customerIcon}>
                            <Popup>
                              <div dir="rtl" className="text-sm">
                                <p className="font-bold">{d.customer_market || "الزبون"}</p>
                                {d.active_order?.address && <p className="text-xs">{d.active_order.address}</p>}
                              </div>
                            </Popup>
                          </Marker>
                          <Polyline
                            positions={[[d.lat, d.lng], [d.customer_lat, d.customer_lng]]}
                            pathOptions={{ color: "#3B82F6", weight: 3, dashArray: "8 6", opacity: 0.75 }}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </MapContainer>

              {/* Empty state overlay */}
              {onMap.length === 0 && !onlySelected && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur border border-[#F5A623]/40 rounded-2xl px-4 py-2 shadow-lg flex items-center gap-2 z-[400]"
                >
                  <MapPin className="w-4 h-4 text-[#F5A623]" />
                  <p className="text-xs font-bold text-[#1E3A5F]">لا يوجد سائق متصل حالياً</p>
                </motion.div>
              )}
            </div>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div
        className="bg-white rounded-2xl overflow-hidden flex flex-col shadow-md border-2 border-[#E4EBE6]"
        dir="rtl"
        data-testid="live-tracking-embed"
      >
        {inner}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-2 sm:p-4"
      dir="rtl"
      data-testid="live-drivers-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {inner}
      </div>
    </div>
  );
};

export default LiveDriversTracking;
